import {useDispatch, useSelector} from 'react-redux';
import {useState} from 'react';
import {AppButton} from '@components/Core/buttons/AppButton/AppButton';
import {useIntl} from 'react-intl';
import {HiOutlineExternalLink} from '@react-icons/all-files/hi/HiOutlineExternalLink';
import {HiOutlineX} from '@react-icons/all-files/hi/HiOutlineX';
import {AppIconButton} from '@components/Core/buttons/AppIconButton/AppIconButton';
import './infoArticle.less';
import {Tooltip} from '@material-ui/core';
import {isVsFeatureEnabled} from '@utils/helpers';
import {VS_FEATURES} from '@constants/main';
import {selectVSFeatures} from '@components/Login/userSlice';
import {selectConfig} from '@components/appSlice';
import SidePanel from '../SidePanel/SidePanel';
import {ArticleContent} from './InfoArticleContent/InfoArticleContent';
import {SUPPORT_OVERVIEW_PATH, VALIDSIGN_SUPPORT_URL} from './infoArticleConstants';
import {
    selectInfoArticle,
    selectIsFetchingInfoArticle,
    selectSelectedInfoArticleId,
    selectSupportLocale,
    setSelectedInfoArticleIdAndFetchArticle
} from './infoArticleSlice';

interface FullScreenMedia {
    type: MediaType;
    src: string;
}

enum MediaType {
    Image = 'img',
    Video = 'video'
}

export default function InfoArticle() {
    const intl = useIntl();
    const dispatch = useDispatch();
    const selectedArticleId = useSelector(selectSelectedInfoArticleId);
    const article = useSelector(selectInfoArticle);
    const isLoading = useSelector(selectIsFetchingInfoArticle);
    const supportLocale = useSelector(selectSupportLocale);
    const vsFeatures = useSelector(selectVSFeatures);
    const isInAppArticlesEnabled = isVsFeatureEnabled({
        feature: VS_FEATURES.IN_APP_ARTICLES,
        vsFeatures
    });
    const [fullScreenMedia, setFullScreenMedia] = useState<FullScreenMedia | null>(null);
    const isOpen = !!selectedArticleId && !!article;
    const supportEmail = intl.formatMessage({id: 'page.login.emailLink', defaultMessage: ''});
    const supportOverviewUrl = `${VALIDSIGN_SUPPORT_URL}/${supportLocale}${SUPPORT_OVERVIEW_PATH}`;

    const handleClose = () => {
        if (fullScreenMedia) {
            setFullScreenMedia(null);
        } else {
            dispatch(setSelectedInfoArticleIdAndFetchArticle(null));
        }
    };

    const handleMediaClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const target = event.target as HTMLElement;
        const tagName = target.tagName.toLowerCase();

        if (tagName === MediaType.Image || tagName === MediaType.Video) {
            return setFullScreenMedia({
                type: tagName === MediaType.Video ? MediaType.Video : MediaType.Image,
                src: tagName === MediaType.Video ? target.currentSrc : target.src
            });
        }
        return null;
    };

    const handleFullScreenMediaKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setFullScreenMedia(null);
        }
    };

    const handleContactSupport = () => window.open(supportOverviewUrl, '_blank');

    const buttonsNode = (
        <AppButton
            href={`mailto:${supportEmail}`}
            className="contact-support-button"
        >
            {intl.formatMessage({id: 'shared.sendUsEmail', defaultMessage: ''})}
        </AppButton>
    );

    const titleNode = (
        <div className="title-node">
            <div className="title-block">
                <h2 className="title">
                    {article?.title || ''}
                </h2>
            </div>
            <div className="header-icons-container">
                <Tooltip
                    placement="top"
                    title={intl.formatMessage({id: 'shared.browseAllArticles', defaultMessage: ''})}
                    arrow
                >
                    <AppIconButton
                        className="header-icon-container"
                        onClick={handleContactSupport}
                    >
                        <HiOutlineExternalLink className="icon" />
                    </AppIconButton>
                </Tooltip>
                <AppIconButton
                    className="header-icon-container"
                    onClick={handleClose}
                    aria-label={intl.formatMessage({id: 'esl.generic.close', defaultMessage: ''})}
                >
                    <HiOutlineX className="close-icon" aria-hidden />
                </AppIconButton>
            </div>
        </div>
    );

    if (!isInAppArticlesEnabled) {
        return null;
    }

    return (
        <>
            <SidePanel
                anchor="right"
                open={isOpen}
                onCloseAway={handleClose}
                classes={{root: 'side-panel info-article-side-panel-container'}}
                PaperProps={{
                    'aria-label': article?.title,
                    className: 'info-article-drawer'
                }}
                titleNode={titleNode}
                buttonsNode={buttonsNode}
                isLoading={isLoading}
            >
                <div className="info-article-content">
                    <ArticleContent
                        htmlContent={article?.body}
                        onClick={handleMediaClick}
                        onKeyDown={handleMediaClick}
                    />
                </div>
            </SidePanel>
            {fullScreenMedia && (
                <div
                    className="fullscreen-article-media-overlay"
                    onClick={() => setFullScreenMedia(null)}
                    onKeyDown={handleFullScreenMediaKeyDown}
                    role="button"
                    tabIndex={0}
                    aria-label={intl.formatMessage({id: 'esl.generic.close'})}
                >
                    {fullScreenMedia.type === MediaType.Image ? (
                        <img
                            src={fullScreenMedia.src}
                            alt=""
                            className="fullscreen-article-media"
                        />
                    ) : (
                        <video
                            src={fullScreenMedia.src}
                            className="fullscreen-article-media"
                            controls
                            autoPlay
                        />
                    )}
                </div>
            )}
        </>
    );
}
